import { forwardRef, HTMLAttributes } from 'react'
import { SvgIcon, SvgIconType } from '@integration-app/react'
import clsx from 'clsx'

import { Tooltip } from 'components/Tooltip'
import { useEventStopPropagation } from 'hooks/useEventStopPropagation'

import classes from './ActionBar.module.css'

function ActionBarRoot({ children }) {
  return <div className={clsx(classes.actionBar)}>{children}</div>
}

interface IActionBarGenericButton extends HTMLAttributes<HTMLButtonElement> {
  iconType?: SvgIconType
  tooltip?: string
}

const ActionBarGenericButton = forwardRef<
  HTMLButtonElement,
  IActionBarGenericButton
>(({ className, iconType, tooltip, onClick, ...props }, ref) => {
  const handleOnClick = useEventStopPropagation((e) =>
    onClick?.(e as React.MouseEvent<HTMLButtonElement, MouseEvent>),
  )

  const button = (
    <button
      type={'button'}
      className={clsx(classes.actionBarButton, className)}
      onClick={handleOnClick}
      {...props}
      ref={ref}
    >
      {iconType && <SvgIcon type={iconType} />}
    </button>
  )

  if (tooltip) {
    return <Tooltip tooltip={tooltip}>{button}</Tooltip>
  }

  return button
})
ActionBarGenericButton.displayName = 'ActionBarGenericButton'

const ActionBarDeleteButton = forwardRef<
  HTMLButtonElement,
  Omit<IActionBarGenericButton, 'iconType'>
>(({ className, ...props }, ref) => {
  return (
    <ActionBarGenericButton
      iconType={SvgIconType.Close}
      tooltip={'Delete'}
      aria-label={'Delete'}
      className={clsx(className, classes.actionBarButton__delete)}
      {...props}
      ref={ref}
    />
  )
})
ActionBarDeleteButton.displayName = 'ActionBarDeleteButton'

export const ActionBar = Object.assign(ActionBarRoot, {
  GenericButton: ActionBarGenericButton,
  DeleteButton: ActionBarDeleteButton,
})
